import qs from "qs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useApiOperation } from "src/api/hooks";
import { getOrgList } from "src/api/organization/org-api";
import { MbOrg, OrgListParams } from "src/api/organization/org-types";
import { Order, PageMeta, Sort } from "src/api/public-types";
import { BaseButton, BaseInputWithSearch, BaseSelect, BaseTable } from "src/components";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import useNavigate from "src/hooks/usePartnerNavigate";
import PagePath from "src/pagePath.json";
import { qsParse } from "src/utils";
import OrgColumns from "./columns/OrganizationColumns";
import SearchResetButton from "src/components/SearchResetButton";
import { BizLocationType } from "../org-types";

// 지정검색 - 검색어 옵션 (value 가 곧 API 요청 파라미터명)
const searchTypes = [
  {
    label: "전체",
    value: "",
  },
  {
    label: "계약자 ID",
    value: "id",
  },
  {
    label: "계약자(법인명/상호)",
    value: "containsName",
  },
  {
    label: "대표자/성명",
    value: "containsCeoName",
  },
  {
    label: "사업자 등록번호",
    value: "containsBizRegNo",
  },
  {
    label: "법인 등록번호",
    value: "containsCorRegNo",
  },
];

// 지정검색 옵션이 바뀌어도 URL 에 남는 이전 옵션의 검색 파라미터 (요청 전 제거 대상)
const searchParamKeys = ["id", "containsName", "containsCeoName", "containsBizRegNo", "containsCorRegNo", "search002"] as const;

const classificationOptions = [
  {
    label: "전체",
    value: "",
  },
  {
    label: "사업자",
    value: "GENERAL_BIZ",
  },
  {
    label: "소유주",
    value: "OWNER",
  },
  {
    label: "개인",
    value: "INDIVIDUAL",
  },
];

const businessOptions = [
  {
    label: "전체",
    value: "",
  },
  {
    label: "법인 사업자",
    value: "CORP",
  },
  {
    label: "개인 사업자",
    value: "SP",
  },
];

// 조건검색 - 국가
const bizLocationTypeOptions = [
  {
    label: "전체",
    value: "",
  },
  {
    label: "국내",
    value: BizLocationType.DOMESTIC,
  },
  {
    label: "해외",
    value: BizLocationType.OVERSEAS,
  },
];

type QueryParams = {
  page?: number;
  size?: number;
  searchType?: string;
  keyword?: string;
  search002?: string;
  sort?: Sort;
  classification?: string;
  bizType?: string;
  bizLocationType?: string;
  id?: string;
  containsName?: string;
  containsCeoName?: string;
  containsBizRegNo?: string;
  containsCorRegNo?: string;
  search001?: string;
  partnerName?: string;
};

const OrganizationList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { executeAsync: getOrgListApi } = useApiOperation(getOrgList);
  const [organizations, setOrganizations] = useState<MbOrg[]>();
  const [pageMeta, setPageMeta] = useState<PageMeta>();
  const { isAuthority } = usePartnerAuthority();
  const queryParams: QueryParams = useMemo(() => {
    const _queryParams: QueryParams = qsParse(location.search);
    // page, size, sort 없이 최초 진입했을때 default 값 바인딩
    if (!_queryParams?.page) {
      _queryParams.page = 0;
    }
    if (!_queryParams?.size) {
      _queryParams.size = 20;
    }
    if (!_queryParams?.sort) {
      _queryParams.sort = {
        orders: [{ property: "id", direction: "DESC" }],
      };
    }

    // qs.parse에 decoder때문에 검색값 입력 검색시 한번더 decoding
    if (_queryParams.keyword) {
      _queryParams.keyword = decodeURIComponent(_queryParams.keyword);
    }

    return _queryParams;
  }, [location]);

  const [params, setParams] = useState<QueryParams>({ ...queryParams });

  // 쿼리파라미터 정보와 함께 네비게이션
  const navigateWithQueryParams = useCallback(
    (passParams?: QueryParams) => {
      let newQueryParams = { ...params, ...(passParams || {}) };
      // 검색키워드 하이픈 제거
      if (newQueryParams.keyword && newQueryParams.keyword !== "") {
        newQueryParams = { ...params, ...(passParams || {}), keyword: newQueryParams.keyword.replace(/-/g, "") };
      }
      const newQueryParamStr = qs.stringify(newQueryParams, { allowDots: true });
      navigate(location.pathname + "?" + decodeURIComponent(newQueryParamStr));
    },
    [navigate, location, params],
  );

  useEffect(() => {
    setParams({ ...queryParams });
    async function fetchProducts(orgtListParams: OrgListParams) {
      const { data } = await getOrgListApi(orgtListParams);
      setOrganizations(data?.data?.content || []);
      setPageMeta(data?.meta?.pageMeta);
    }
    // let orgListParams: OrgListParams = {
    //   page: queryParams.page,
    //   size: queryParams.size,
    //   sort: queryParams.sort,
    // };
    // 이전 지정검색 옵션의 파라미터가 URL 에 남아 함께 전송되지 않도록 초기화 후 현재 옵션만 세팅
    const searchQueryParams = queryParams as Record<string, unknown>;
    searchParamKeys.forEach((key) => delete searchQueryParams[key]);

    if (queryParams.searchType && queryParams.searchType !== "") {
      searchQueryParams[queryParams.searchType] = queryParams.keyword || "";
    } else {
      queryParams.search002 = queryParams.keyword || "";
    }
    const cleanPayload = Object.fromEntries(Object.entries(queryParams).filter(([_, v]) => v !== null && v !== undefined && v !== ""));

    fetchProducts(cleanPayload);
  }, [getOrgListApi, queryParams, location.search]);

  return (
    <div className="page-product-list">
      <div className="contents-container__table">
        <div className="contents-container__search-wrap">
          <div className="left-area">
            {/* section 태그가 지정검색, 조건검색, 영역이고 반드시 필요함 */}
            <section>
              <div className="left-area__index">
                <span>지정검색</span>
              </div>
              <div className="left-area__contents">
                <BaseInputWithSearch
                  type="text"
                  selectValue={params.searchType}
                  inputValue={params?.keyword || ""}
                  stateOptions={searchTypes}
                  setStateValue={(searchType: string) => {
                    setParams({ ...params, searchType });
                  }}
                  onClearClick={() => navigateWithQueryParams({ ...params, keyword: "" })}
                  onChange={(keyword: string) => setParams({ ...params, keyword })}
                  onKeyUp={() => navigateWithQueryParams({ page: 0 })}
                  onSearchClick={() => navigateWithQueryParams({ page: 0 })}
                />
              </div>
            </section>
            <section>
              <div className="left-area__index">
                <span>조건검색</span>
              </div>
              <div className="left-area__contents">
                <div className="minmax160">
                  <BaseSelect
                    value={params.classification}
                    stateOptions={classificationOptions}
                    placeholder="계약자 유형"
                    setStateValue={(type?: string) => {
                      setParams({ ...params, classification: type });
                      navigateWithQueryParams({ ...params, page: 0, classification: type });
                    }}
                  />
                </div>
                <div className="minmax160">
                  <BaseSelect
                    value={params.bizLocationType}
                    stateOptions={bizLocationTypeOptions}
                    placeholder="국가"
                    setStateValue={(type?: string) => {
                      setParams({ ...params, bizLocationType: type });
                      navigateWithQueryParams({ ...params, page: 0, bizLocationType: type });
                    }}
                  />
                </div>
                <div className="minmax160">
                  <BaseSelect
                    value={params.bizType}
                    stateOptions={businessOptions}
                    placeholder="사업자 유형"
                    setStateValue={(type?: string) => {
                      setParams({ ...params, bizType: type });
                      navigateWithQueryParams({ ...params, page: 0, bizType: type });
                    }}
                  />
                </div>
                <SearchResetButton />
              </div>
            </section>
          </div>
          {isAuthority("w") && (
            <div className="right-area">
              <BaseButton
                title="+ 계약자 등록"
                onClick={() => {
                  navigate(PagePath.organization.form);
                }}
              />
            </div>
          )}
        </div>
        {organizations && (
          <BaseTable
            data={organizations}
            columns={OrgColumns}
            pageIndex={pageMeta?.pageRequest.page || 0}
            totalPages={pageMeta?.totalPages || 0}
            goPage={(page: number) => {
              navigateWithQueryParams({ page });
            }}
            totalElements={Number(pageMeta?.totalElements) || 0}
            orders={queryParams.sort?.orders}
            disabledSortHeaders={["classifications", "bizType"]}
            sizeOption={(number: number) => {
              setParams({ ...params, size: number });
              navigateWithQueryParams({ ...params, page: 0, size: number });
            }}
            currentSize={Number(params.size)}
            setOrders={(orders?: Array<Order>) => {
              if (orders) {
                navigateWithQueryParams({ sort: { orders } });
              }
            }}
          />
        )}
      </div>
    </div>
  );
};

export default OrganizationList;
